// Composant principal du tableau de bord — 3 KPIs + 2 LineChart Recharts.
//
// Un seul appel `/api/dashboard/summary` au montage. La page HomePage
// embarque ce composant ; il est autonome vis-à-vis du routing et des stores.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ApiError } from "../api/client";
import { formatAmount, compactAmount, currencySymbol } from "../utils/currency";
import { dashboardApi } from "../api/dashboard";
import { invoicesApi } from "../api/invoices";
import type { DashboardSummary, InvoiceListItem, InvoiceStatus } from "../api/types";

type State =
  | { kind: "loading" }
  | { kind: "ok"; data: DashboardSummary; invoices: InvoiceListItem[] }
  | { kind: "error"; message: string };

/**
 * "2026-04" → "avr. 2026". Intl.DateTimeFormat est dispo partout.
 * On passe par une date "sûre" (le 15 du mois) pour éviter les décalages
 * de fuseau en bord de mois.
 */
function formatMonthLabel(ym: string): string {
  const [year, month] = ym.split("-").map((s) => Number(s));
  if (!year || !month) return ym;
  const d = new Date(year, month - 1, 15);
  return new Intl.DateTimeFormat("fr-FR", {
    month: "short",
    year: "2-digit",
  }).format(d);
}

export function DashboardHome() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    Promise.all([
      dashboardApi.summary(selectedCurrency ?? undefined),
      invoicesApi.list({ limit: 8, ...(selectedCurrency ? { currency: selectedCurrency } : {}) } as Parameters<typeof invoicesApi.list>[0]),
    ])
      .then(([data, inv]) => {
        if (!cancelled) setState({ kind: "ok", data, invoices: inv.items });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message =
          e instanceof ApiError
            ? e.detail
            : e instanceof Error
              ? e.message
              : t("common.unknownError");
        setState({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCurrency]);

  if (state.kind === "loading") {
    return (
      <div
        role="status"
        aria-label={t("dashboard.loading")}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 rounded-xl bg-slate-100 animate-pulse"
            />
          ))}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="h-72 rounded-xl bg-slate-100 animate-pulse" />
          <div className="h-72 rounded-xl bg-slate-100 animate-pulse" />
        </div>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
      >
        {t("dashboard.errorLoad")} : {state.message}
      </div>
    );
  }

  const { kpis, activity_series, cashflow_series, currency, available_currencies } = state.data;
  const safeActivitySeries = activity_series ?? [];
  const safeCashflowSeries = cashflow_series ?? [];
  const displayCurrency = selectedCurrency ?? currency;

  // Recharts préfère travailler avec des number, pas des Decimal string.
  // Le backend renvoie 12 mois passés + 3 mois futurs (échéances à venir).
  const activityData = safeActivitySeries.map((p) => ({
    month: p.month,
    label: formatMonthLabel(p.month),
    issued: Number(p.issued_ttc),
  }));
  const cashflowData = safeCashflowSeries.map((p) => ({
    month: p.month,
    label: formatMonthLabel(p.month),
    collected: Number(p.collected),
    due: Number(p.due),
  }));

  return (
    <div className="space-y-6">
      {/* -------- Filtre devise -------------------------------------- */}
      {available_currencies.length > 1 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500">{t("dashboard.currencyLabel")}</span>
            <button
              onClick={() => setSelectedCurrency(null)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedCurrency === null
                  ? "bg-brand-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {t("dashboard.currencyAll")}
            </button>
            {available_currencies.map((cur) => (
              <button
                key={cur}
                onClick={() => setSelectedCurrency(cur)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCurrency === cur
                    ? "bg-brand-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {cur}
              </button>
            ))}
          </div>
          {selectedCurrency === null && (
            <p className="text-xs text-amber-600">
              {t("dashboard.currencyWarning")}
            </p>
          )}
        </div>
      )}

      {/* -------- KPIs ---------------------------------------------- */}
      <section
        aria-label="Indicateurs clés"
        className="space-y-3"
      >
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("dashboard.sectionClient")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            label={t("dashboard.kpiPending")}
            value={formatAmount(kpis.pending_amount, displayCurrency)}
            tone="neutral"
            hint={t("dashboard.kpiPendingHint")}
          />
          <KpiCard
            label={t("dashboard.kpiOverdue")}
            value={formatAmount(kpis.overdue_amount, displayCurrency)}
            tone="red"
            hint={t("dashboard.kpiOverdueHint")}
          />
          <KpiCard
            label={t("dashboard.kpiCollected")}
            value={formatAmount(kpis.collected_this_month, displayCurrency)}
            tone="green"
            hint={t("dashboard.kpiCollectedHint")}
          />
        </div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">{t("dashboard.sectionSupplier")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KpiCard
            label={t("dashboard.kpiSupplierPending")}
            value={formatAmount(kpis.supplier_pending ?? "0", displayCurrency)}
            tone="red"
            hint={t("dashboard.kpiSupplierPendingHint")}
          />
          <KpiCard
            label={t("dashboard.kpiSupplierPaid")}
            value={formatAmount(kpis.supplier_paid_this_month ?? "0", displayCurrency)}
            tone="neutral"
            hint={t("dashboard.kpiSupplierPaidHint")}
          />
        </div>
      </section>

      {/* -------- Charts -------------------------------------------- */}
      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title={t("dashboard.activityTitle")} subtitle={t("dashboard.activitySubtitle")}>
          <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-[#1e3a5f]" /> {t("dashboard.activityBilled")}</span>
          </div>
          <div data-testid="chart-activity" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activityData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" fontSize={12} tick={{ fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis
                  fontSize={12}
                  tick={{ fill: "#64748b" }}
                  tickFormatter={(v: number) => compactAmount(v, displayCurrency)}
                  width={70}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip formatter={(v: number) => formatAmount(String(v), displayCurrency)} />
                <Bar
                  dataKey="issued"
                  name={t("dashboard.activityBilled")}
                  fill="#1e3a5f"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
                <Brush dataKey="label" height={25} stroke="#cbd5e1" fill="#f8fafc" travellerWidth={10} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title={t("dashboard.cashflowTitle")} subtitle={t("dashboard.cashflowSubtitle")}>
          <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-amber-400" /> {t("dashboard.cashflowDue")}</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-600" /> {t("dashboard.cashflowCollected")}</span>
          </div>
          <div data-testid="chart-cashflow" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cashflowData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" fontSize={12} tick={{ fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis
                  fontSize={12}
                  tick={{ fill: "#64748b" }}
                  tickFormatter={(v: number) => compactAmount(v, displayCurrency)}
                  width={70}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip formatter={(v: number) => formatAmount(String(v), displayCurrency)} />
                <Bar
                  dataKey="due"
                  name={t("dashboard.cashflowDue")}
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={35}
                />
                <Bar
                  dataKey="collected"
                  name={t("dashboard.cashflowCollected")}
                  fill="#059669"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={35}
                />
                <Brush dataKey="label" height={25} stroke="#cbd5e1" fill="#f8fafc" travellerWidth={10} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </section>

      {/* -------- Factures récentes --------------------------------- */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">{t("dashboard.recentInvoices")}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-blue-700 uppercase border-b border-slate-100">
                <th className="text-left py-2 font-medium">{t("dashboard.colRef")}</th>
                <th className="text-left py-2 font-medium">{t("dashboard.colClient")}</th>
                <th className="text-left py-2 font-medium">{t("dashboard.colProject")}</th>
                <th className="text-left py-2 font-medium">{t("dashboard.colIntervenant")}</th>
                <th className="text-left py-2 font-medium">{t("dashboard.colDueDate")}</th>
                <th className="text-right py-2 font-medium">{t("dashboard.colAmountHT")}</th>
                <th className="text-center py-2 font-medium">{t("dashboard.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {state.invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-3 font-medium text-slate-900">{inv.reference}</td>
                  <td className="py-3 text-slate-700">{inv.client_name}</td>
                  <td className="py-3"><span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded">{inv.project_name ?? "—"}</span></td>
                  <td className="py-3 text-slate-700">{inv.intervenant_name ?? "—"}</td>
                  <td className="py-3 text-slate-600">{new Date(inv.due_date).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB")}</td>
                  <td className="py-3 text-right font-semibold text-slate-900">{formatAmount(inv.amount_ht, inv.currency)}</td>
                  <td className="py-3 text-center"><StatusBadge status={inv.effective_status} /></td>
                </tr>
              ))}
              {state.invoices.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400">{t("common.noInvoices")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const { t } = useTranslation();
  const config: Record<string, { label: string; bg: string; text: string }> = {
    issued: { label: t("status.issued"), bg: "bg-blue-50", text: "text-blue-700" },
    partial: { label: t("status.partial"), bg: "bg-amber-50", text: "text-amber-700" },
    paid: { label: t("status.paid"), bg: "bg-emerald-50", text: "text-emerald-700" },
    overdue: { label: t("status.overdue"), bg: "bg-red-50", text: "text-red-600" },
    draft: { label: t("status.draft"), bg: "bg-slate-100", text: "text-slate-500" },
    cancelled: { label: t("status.cancelled"), bg: "bg-slate-100", text: "text-slate-400" },
  };
  const c = config[status] ?? { label: status, bg: "bg-slate-100", text: "text-slate-600" };
  return (
    <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sous-composants
// ---------------------------------------------------------------------------

function KpiCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone: "neutral" | "green" | "red";
  hint: string;
}) {
  const toneClass =
    tone === "green"
      ? "text-emerald-700"
      : tone === "red"
        ? "text-red-700"
        : "text-slate-900";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="text-sm text-slate-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold ${toneClass}`}
        data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {value}
      </div>
      <div className="mt-2 text-xs text-slate-400">{hint}</div>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-medium text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500 mb-3">{subtitle}</p>
      {children}
    </div>
  );
}


